import { LocationStrategy, Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

@Injectable()
export class CarmRouter {

  private nativeBn9Key = '{NATIVE_BN9}';
  private bn15Key = '{BN15}';

  private readonly navigationRequest = new BehaviorSubject<boolean>(false); //more reliable than route param change events

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private locationStrategy: LocationStrategy) {

  }

  carmNavigate(segments: string[], extras?: NavigationExtras): Promise<boolean> {
    const tempSegments = this.buildSegments(segments);

    return this.router.navigate(tempSegments, extras).then(nav => {
      this.navigationRequest.next(true);

      return true;
    });
  }

  carmNavigateByCustomBusinessNumbers(nativeBn9: string, bn15: string, segments: string[], extras?: NavigationExtras): Promise<boolean> {

    const tempSegments = this.buildSegmentsByCustomBusinessNumbers(nativeBn9, bn15, segments);

    return this.router.navigate(tempSegments, extras).then(nav => {
      this.navigationRequest.next(true);

    return true;
    });
  }

  public buildSegments(segments: string[]): string[]{
    let tempSegments: string[] = [];

    if (!this.bn9 && !this.bn15) {
      segments = this.prepareOrphanSegments(segments);
    }

    segments.forEach(segment => {
      let tempSegment = segment.replace(this.nativeBn9Key, this.nativeBn9);
      tempSegment = tempSegment.replace(this.bn15Key, this.bn15);

      let newSegments = tempSegment.split('/');
      tempSegments.push(...newSegments);
    });

    return tempSegments;
  }

  private buildSegmentsByCustomBusinessNumbers(nativeBn9: string, bn15: string, segments: string[]): string[]{

    let tempSegments: string[] = [];

    segments.forEach(segment => {

      let tempSegment = segment.replace(this.nativeBn9Key, nativeBn9);
      tempSegment = tempSegment.replace(this.bn15Key, bn15);

      let newSegments = tempSegment.split('/');
      tempSegments.push(...newSegments);
    });

    return tempSegments;
  }

  public get navigationRequest$(): Observable<boolean> {
    return this.navigationRequest.asObservable();
  }

  private get navigationRequested(): boolean {
    return this.navigationRequest.value;
  }

  public buildHref(segments: string[]): string{
    const baseHref = this.locationStrategy.getBaseHref();
    const urlTree = this.router.createUrlTree(this.buildSegments(segments))
    return baseHref + this.router.serializeUrl(urlTree).substring(1); //remove first chars
  }

  private prepareOrphanSegments(segments: string[]): string[] {
    const hasBn9InSegment = segments.some(segment => segment.length === 9 && !isNaN(+segment));
    const isRouteConst = segments.length == 1 || segments.length > 1 && segments[0].includes('/') && !hasBn9InSegment;
      if (isRouteConst) {
        const newSegments = segments.slice();
        newSegments[0] = newSegments[0].toOrphanUrl();
        return newSegments.slice();
      } else {
        if (hasBn9InSegment){
          return segments;
        }
        // remove this -> nb/{NATIVE_BN9}/p/{BN15}/
        return segments.filter(segment => segment !== 'nb' && segment !== this.nativeBn9Key && segment !== 'p' && segment !== this.bn15Key);
      }
  }

  public back(fallbackUrl: string[], extras?: NavigationExtras): void {
    const hasHistory = this.navigationRequested;

    if(hasHistory){
      this.location.back();
    }else{
      this.carmNavigate(fallbackUrl, extras);
    }
  }

  get nativeBn9(): string {
    return this.route?.root?.firstChild?.snapshot?.params['nativeBn9'];
  }

  get nativeBn9$(): Observable<string> {
    return this.navigationRequest$.pipe(map( x => { return this.nativeBn9}), distinctUntilChanged());
  }

  get bn15(): string {
    return this.route?.root?.firstChild?.snapshot?.params['bn15'];
  }

  get bn15$(): Observable<string> {
    return this.navigationRequest$.pipe(map( x => { return this.bn15}), distinctUntilChanged());
  }

  get bn9(): string {
    return this.bn15?.substring(0, 9);
  }

  get bn9$(): Observable<string> {
    return this.navigationRequest$.pipe(map( x => { return this.bn9}), distinctUntilChanged());
  }

  get programNumber(): string {
    return this.bn15?.substring(9);
  }

  get programNumber$(): Observable<string> {
    return this.bn15$?.pipe(map(bn15 => bn15?.substring(9)), distinctUntilChanged());
  }

  get events(): Observable<any> {
    return this.router?.events;
  }
}
